/**
 * Type definitions for the Send Action to Device feature
 * Feature: 003-c-program-files
 */

/**
 * Represents the action currently active on a child's device.
 * Stored in Firebase at /devices/{deviceId}/status/currentAction
 */
export interface CurrentAction {
  /** ID of the action being sent (references Action from feature 002) */
  actionId: string;

  /** Unix timestamp in milliseconds when action should start */
  startTimestamp: number;

  /** Whether action requires parent approval to complete */
  stayUntilParentApproves: boolean;

  /** Whether action requires child to mark as complete */
  stayUntilChecked: boolean;

  /** Duration in minutes for fixed-time actions (0 if not applicable) */
  duration: number;

  /**
   * Absent means 'active'. 'awaitingApproval': the child ticked an action with
   * stayUntilParentApproves, and it waits for the parent's Approve (BA-58)
   */
  state?: CurrentActionState;

  /** When the child ticked it (ms, server time), set with state 'awaitingApproval' */
  doneAt?: number;
}

export type CurrentActionState = 'active' | 'awaitingApproval';

export const isAwaitingApproval = (action: CurrentAction | null | undefined): boolean =>
  action?.state === 'awaitingApproval';

/**
 * Form data for sending/editing a currentAction.
 * This is what the dialog works with before converting to CurrentAction.
 */
export interface CurrentActionFormData {
  /** Time in minutes from now when action should start */
  timeToStart: number;

  /** Whether action requires parent approval to complete */
  stayUntilParentApproves: boolean;

  /** Whether action requires child to mark as complete */
  stayUntilChecked: boolean;

  /** Whether action should have fixed duration */
  stayUntilFixedTime: boolean;

  /** Duration in minutes (only used if stayUntilFixedTime is true) */
  duration: number;
}

/**
 * Request payload for sending/editing a currentAction via API
 */
export interface SendCurrentActionRequest {
  /** ID of the action to send */
  actionId: string;

  /** Time in minutes from now when action should start */
  timeToStart: number;

  /** Whether action requires parent approval to complete */
  stayUntilParentApproves: boolean;

  /** Whether action requires child to mark as complete */
  stayUntilChecked: boolean;

  /** Duration in minutes (0 if not applicable) */
  duration: number;
}

/**
 * Approve / done carry the action's identity, so a call about an action that has
 * since been replaced gets a 409 instead of ending the new one
 */
export interface CurrentActionRef {
  actionId: string;
  startTimestamp: number;
}
